import axios from "axios";

const getApiUrl = () => {
  return process.env.REACT_APP_ENV === "production"
    ? `${process.env.REACT_APP_API_URL_DEPLOYED}/`
    : `${process.env.REACT_APP_API_URL_LOCAL}/`;
};

export const API_BASE_URL = getApiUrl();
export const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    "Cache-Control": "no-cache",
    Pragma: "no-cache",
    Expires: "0",
    Accept: "application/json",
  },
});

// Add a request interceptor
api.interceptors.request.use(
  (config) => {
    // Only add the Authorization header if it's not already present
    if (!config.headers.Authorization) {
      const token = localStorage.getItem("token");

      // Add the Authorization header if token exists
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    }

    return config;
  },
  (error) => {
    // Handle request errors here
    return Promise.reject(error);
  },
);

// Track if we're currently refreshing the token to avoid multiple refresh attempts
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });

  failedQueue = [];
};

// Add a response interceptor to handle token-related errors
api.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    // Handle unauthorized errors (e.g., token expired)
    if (
      error.response &&
      error.response.status === 401 &&
      !originalRequest._retry
    ) {
      // Don't try to refresh if the failed request was to the refresh endpoint
      if (originalRequest.url === "/api/auth/refresh") {
        // Refresh token is invalid, clear everything and redirect to login
        localStorage.removeItem("token");
        const currentPath = encodeURIComponent(window.location.pathname + window.location.search);
        window.location.href = `/login?next=${currentPath}`;
        return Promise.reject(error);
      }

      if (isRefreshing) {
        // If already refreshing, queue this request
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            return api(originalRequest);
          })
          .catch((err) => {
            return Promise.reject(err);
          });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        // Try to refresh the token
        const response = await api.post("/api/auth/refresh");
        const { token } = response.data;

        // Update token in localStorage
        localStorage.setItem("token", token);

        // Update authorization header for the original request
        originalRequest.headers.Authorization = `Bearer ${token}`;

        // Process any queued requests
        processQueue(null, token);

        // Retry the original request
        return api(originalRequest);
      } catch (refreshError) {
        // Refresh failed, clear token and redirect to login
        processQueue(refreshError, null);
        localStorage.removeItem("token");
        const currentPath = encodeURIComponent(window.location.pathname + window.location.search);
        window.location.href = `/login?next=${currentPath}`;
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  },
);

// Instant Video Call API methods
export const instantVideoCallApi = {
  /**
   * Create an instant video interview
   * @param {Object} candidateData - Candidate information
   * @param {Object} ownerData - Owner/interviewer information
   * @param {Object} companyData - Company information
   * @returns {Promise<Object>} Interview details with invite links
   */
  createInstantVideoCall: async (candidateData, companyData) => {
    try {
      console.log('API - candidateData:', candidateData);
      console.log('API - companyData:', companyData);
      
      const requestData = {
        candidate: {
          candidateId: candidateData.id || candidateData._id,
          email: candidateData.email,
          name: candidateData.name,
          phone: candidateData.phone || null
        },
        jobId: companyData.jobId || null,
        jobTitle: companyData.jobTitle || 'Instant Interview',
        interviewType: companyData.interviewType || 'technical'
      };

      console.log('API - requestData being sent:', requestData);

      const response = await api.post('/video/interview/instant', requestData);
      return response.data;
    } catch (error) {
      console.error('Failed to create instant video call:', error);
      throw error;
    }
  },

  /**
   * Send instant interview email to candidate
   * @param {string} roomId - Room ID
   * @param {string} candidateId - Candidate ID
   * @param {string} email - Candidate email
   * @returns {Promise<Object>} Email sending result
   */
  sendInstantVideoCallEmail: async (roomId, candidateId, email) => {
    try {
      const requestData = {
        roomId,
        candidateId,
        email
      };

      const response = await api.post('/video/interview/instant/send-email', requestData);
      return response.data;
    } catch (error) {
      console.error('Failed to send instant video call email:', error);
      throw error;
    }
  }
};

// Schedule Interview API methods
export const scheduleInterviewApi = {
  /**
   * Fetch all schedule interviews for the company
   * @param {Object} filters - Filter options
   * @returns {Promise<Object>} Interview list with pagination
   */
  fetchAllInterviews: async (filters = {}) => {
    try {
      const queryParams = new URLSearchParams();
      
      // Add filters to query params
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== null && value !== undefined && value !== '') {
          queryParams.append(key, value);
        }
      });

      const response = await api.get(`/api/schedule-interviews/all?${queryParams.toString()}`);
      return response.data;
    } catch (error) {
      console.error('Failed to fetch schedule interviews:', error);
      throw error;
    }
  },

  /**
   * Delete a schedule interview and its linked video room
   * @param {string} id - Schedule interview ID
   * @returns {Promise<Object>} Delete result
   */
  deleteScheduleInterview: async (id) => {
    try {
      const response = await api.delete(`/api/schedule-interviews/${id}`);
      return response.data;
    } catch (error) {
      console.error('Failed to delete schedule interview:', error);
      throw error;
    }
  },

  /**
   * Fetch all video rooms
   * @param {Object} filters - Filter parameters
   * @returns {Promise<Object>} Video rooms data
   */
  fetchAllVideoRooms: async (filters = {}) => {
    try {
      const queryParams = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== null && value !== undefined && value !== '') {
          queryParams.append(key, value);
        }
      });
      const response = await api.get(`/api/schedule-interviews/video-rooms?${queryParams.toString()}`);
      return response.data;
    } catch (error) {
      console.error('Failed to fetch video rooms:', error);
      throw error;
    }
  },

  /**
   * Delete a video room
   * @param {string} id - Video room ID
   * @returns {Promise<Object>} Delete result
   */
  deleteVideoRoom: async (id) => {
    try {
      const response = await api.delete(`/api/schedule-interviews/video-room/${id}`);
      return response.data;
    } catch (error) {
      console.error('Failed to delete video room:', error);
      throw error;
    }
  }
};
